import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';

import type { RouteComponentProps } from 'react-router-dom';
import type { StaticContext } from 'react-router';
import type { LocationDescriptor } from 'history';

import { useAppSelector } from '@/hooks';
import routes from '@/config/routes';
import { AuthStatus } from '@/store/enums';
import { selectAuthStatus, selectUser } from '@/store/selectors/authSelectors';

type ComponentProps = RouteComponentProps<{ [p: string]: string | undefined }, StaticContext, unknown>;

const privateRouteSelector = createSelector(selectUser, selectAuthStatus, (user, authStatus) => ({
  user,
  authStatus,
}));

interface IProps {
  component: React.LazyExoticComponent<(props?: ComponentProps) => React.ReactElement>;
  path: string;
  exact?: boolean;
}

const PrivateRoute = ({ component: Component, ...rest }: IProps): React.ReactElement => {
  const { user, authStatus } = useAppSelector(privateRouteSelector);

  function _renderContent(props: ComponentProps) {
    const options: LocationDescriptor = {
      pathname: routes.auth.login,
      ...(props?.location && { state: { from: props.location } }),
    };
    return !user && authStatus === AuthStatus.newSession ? <Redirect to={options} /> : <Component {...props} />;
  }

  return <Route {...rest} render={_renderContent} />;
};

export { PrivateRoute };
